/**
 * Responsive image attributes generator
 * Implements three strategies based on input options
 */

import { buildImageUrl } from './url-builder';
import type { ResponsiveOptions, ResponsiveAttributes, TransformOptions } from './types';

// The delivery edge snaps `w_` to a fixed ladder — widths at or below 240 are
// served exactly, anything larger rounds to the nearest of these rungs (ties go
// up). Breakpoints that are not rungs cost a cache key and return a byte-identical
// image under a different `w` descriptor, so every default below is either <= 240
// or an exact rung.
export const DEFAULT_DEVICE_BREAKPOINTS = [320, 480, 720, 1080, 1440, 1920];
export const DEFAULT_IMAGE_BREAKPOINTS = [16, 32, 48, 64, 96, 128, 240, 320, 480];

/**
 * Parse sizes attribute to extract minimum viewport width
 * Returns the smallest vw value found, or null if none found
 */
function parseMinVWFromSizes(sizes: string): number | null {
  // Match patterns like "33vw", "100vw", "(min-width: 800px) 33vw"
  const vwMatches = sizes.match(/(\d+(?:\.\d+)?)vw/g);
  if (!vwMatches || vwMatches.length === 0) return null;
  
  const vwValues = vwMatches.map(m => parseFloat(m));
  return Math.min(...vwValues);
}

/**
 * Find nearest breakpoint to a given width
 */
function findNearestBreakpoint(width: number, breakpoints: number[]): number {
  return breakpoints.reduce((prev, curr) => {
    return Math.abs(curr - width) < Math.abs(prev - width) ? curr : prev;
  });
}

function normalizeBreakpoints(breakpoints: number[]): number[] {
  const normalized = breakpoints
    .filter((bp): bp is number => Number.isFinite(bp) && bp > 0)
    .map(bp => Math.round(bp));

  if (normalized.length === 0) {
    return [];
  }

  return [...new Set(normalized)].sort((a, b) => a - b);
}

/**
 * Generate responsive image attributes
 * 
 * Rule 1: width only → DPR strategy (1x and 2x)
 * Rule 2: sizes provided → width-based strategy
 * Rule 3: neither → all device breakpoints
 */
export function generateResponsiveAttributes(
  baseUrl: string,
  workspace: string,
  options: ResponsiveOptions,
  defaults?: { f?: string; q?: string | number; [key: string]: any },
  deviceBreakpoints: number[] = DEFAULT_DEVICE_BREAKPOINTS,
  imageBreakpoints: number[] = DEFAULT_IMAGE_BREAKPOINTS,
  enableDPR: boolean = true
): ResponsiveAttributes {
  const { src, width, sizes, transform, breakpoints } = options;

  const normalizedDeviceBreakpoints = normalizeBreakpoints(deviceBreakpoints);
  const normalizedImageBreakpoints = normalizeBreakpoints(imageBreakpoints);
  const customBreakpoints = breakpoints ? normalizeBreakpoints(breakpoints) : null;

  const effectiveDeviceBreakpoints =
    normalizedDeviceBreakpoints.length > 0
      ? normalizedDeviceBreakpoints
      : normalizeBreakpoints(DEFAULT_DEVICE_BREAKPOINTS);
  const effectiveImageBreakpoints =
    normalizedImageBreakpoints.length > 0
      ? normalizedImageBreakpoints
      : normalizeBreakpoints(DEFAULT_IMAGE_BREAKPOINTS);
  
  // Rule 1: width only → DPR strategy
  if (width !== undefined && !sizes) {
    const baseWidth = width;

    // Both candidates are emitted unconditionally. Reading the device ratio here
    // would make the markup depend on the renderer: `getDPR()` is 1 on the server
    // and 2 on a retina client, so the server would ship a one-candidate srcSet
    // and the client would hydrate a two-candidate one — a React attribute
    // mismatch, and a second network fetch when the browser re-picks. Emitting
    // both and letting the browser choose is what `1x`/`2x` descriptors are for.
    const srcSetParts: string[] = [];

    // 1x URL — no `dpr_1`, which is a no-op the origin still caches separately.
    const transform1x: TransformOptions = {
      ...transform,
      w: baseWidth
    };
    const url1x = buildImageUrl(baseUrl, workspace, src, transform1x, defaults, false);
    srcSetParts.push(`${url1x} 1x`);

    if (enableDPR) {
      const transform2x: TransformOptions = {
        ...transform,
        w: baseWidth,
        dpr: 2
      };
      const url2x = buildImageUrl(baseUrl, workspace, src, transform2x, defaults, false);
      srcSetParts.push(`${url2x} 2x`);
    }

    // Default `src` must match the 1x candidate (avoid enableDPR-only drift vs srcSet).
    const baseSrc = url1x;

    return {
      src: baseSrc,
      srcSet: srcSetParts.join(', '),
      width: baseWidth
    };
  }
  
  // Rule 2: sizes provided → width-based strategy
  if (sizes) {
    const minVW = parseMinVWFromSizes(sizes);

    // Prefer user breakpoints, else both ladders. The image ladder alone tops out
    // at 480, so a `sizes="100vw"` hero would offer nothing larger and every
    // viewport above 480 would upscale a 480 px rendition.
    const widthBreakpoints =
      customBreakpoints && customBreakpoints.length > 0
        ? customBreakpoints
        : normalizeBreakpoints([...effectiveImageBreakpoints, ...effectiveDeviceBreakpoints]);
    const smallestViewport = effectiveDeviceBreakpoints[0];
    const minimumRequiredWidth = minVW
      ? Math.max(1, Math.round((minVW / 100) * smallestViewport))
      : 0;
    const filteredBreakpoints = widthBreakpoints.filter(bp => bp >= minimumRequiredWidth);
    const finalBreakpoints =
      filteredBreakpoints.length > 0
        ? filteredBreakpoints
        : [findNearestBreakpoint(Math.max(1, minimumRequiredWidth), widthBreakpoints)];
    
    // Generate srcset with w descriptors. `dpr_` is deliberately not emitted on
    // these: a `w` descriptor already states the candidate's real pixel width, and
    // the browser multiplies by its own ratio when choosing. Adding `dpr_` would
    // double-count the ratio and, because it is read from the device, differ
    // between server and client render.
    const srcSetParts = finalBreakpoints.map(bp => {
      const transformWithWidth: TransformOptions = {
        ...transform,
        w: bp
      };
      const url = buildImageUrl(baseUrl, workspace, src, transformWithWidth, defaults, false);
      return `${url} ${bp}w`;
    });

    // Base URL (use smallest breakpoint or provided width)
    const baseWidth = width || finalBreakpoints[0];
    const baseTransform: TransformOptions = {
      ...transform,
      w: baseWidth
    };
    const baseSrc = buildImageUrl(baseUrl, workspace, src, baseTransform, defaults, false);
    
    return {
      src: baseSrc,
      srcSet: srcSetParts.join(', '),
      sizes: sizes,
      width: baseWidth
    };
  }
  
  // Rule 3: neither width nor sizes → all device breakpoints
  // Use all breakpoints with w descriptors
  const finalBreakpoints =
    customBreakpoints && customBreakpoints.length > 0
      ? customBreakpoints
      : effectiveDeviceBreakpoints;
  // As in Rule 2, `w` descriptors carry the pixel width, so no `dpr_` here.
  const srcSetParts = finalBreakpoints.map(bp => {
    const transformWithWidth: TransformOptions = {
      ...transform,
      w: bp
    };
    const url = buildImageUrl(baseUrl, workspace, src, transformWithWidth, defaults, false);
    return `${url} ${bp}w`;
  });

  // Base URL (use smallest breakpoint)
  const baseWidth = finalBreakpoints[0];
  const baseTransform: TransformOptions = {
    ...transform,
    w: baseWidth
  };
  const baseSrc = buildImageUrl(baseUrl, workspace, src, baseTransform, defaults, false);
  
  return {
    src: baseSrc,
    srcSet: srcSetParts.join(', '),
    sizes: '100vw',
    width: baseWidth
  };
}
